import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Form, Table, Container, InputGroup, Badge } from "react-bootstrap";
import { FiSearch, FiEdit2, FiPlus } from "react-icons/fi";
import { createAppoint, getAllAppoints, updateAppoint } from "../../../api/onboarddeboard";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Appoint = () => {
  const token = localStorage.getItem("token");

  const [appoints, setAppoints] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    appointName: "",
    duration: "",
    probationPeriod: "",
    status: "Active",
  });
  const [errors, setErrors] = useState({});

  const fetchAppoints = useCallback(async () => {
    try {
      const res = await getAllAppoints(token);
      if (res.status === 200) setAppoints(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(() => {
    fetchAppoints();
  }, [fetchAppoints]);

  const handleOpenAddModal = () => {
    setEditId(null);
    setForm({ appointName: "", duration: "", probationPeriod: "", status: "Active" });
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (appoint) => {
    setEditId(appoint.id);
    setForm({
      appointName: appoint.appointName || "",
      duration: appoint.duration || "",
      probationPeriod: appoint.probationPeriod || "",
      status: appoint.status || "Active",
    });
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setErrors({});
    setEditId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleToggleStatus = () => {
    setForm((prev) => ({
      ...prev,
      status: prev.status === "Active" ? "Inactive" : "Active",
    }));
  };

  const validate = () => {
    const e = {};
    const trimmedName = form.appointName.trim();
    const trimmedDuration = form.duration.trim();

    if (!trimmedName) {
      e.appointName = "Appointment Type is required";
    } else if (/^[0-9]+$/.test(trimmedName)) {
      e.appointName = "Appointment Type cannot be numbers only";
    } else if (/^[^a-zA-Z0-9]+$/.test(trimmedName)) {
      e.appointName = "Appointment Type cannot contain only special characters";
    } else if (!/^[A-Za-z]+(?:[\s-][A-Za-z]+)*$/.test(trimmedName)) {
      e.appointName = "Appointment Type must contain only alphabets";
    }

    if (!trimmedDuration) {
      e.duration = "Duration is required";
    }

    return e;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      appointName: form.appointName.trim(),
      duration: form.duration.trim(),
      probationPeriod: form.probationPeriod.trim() || "N/A",
      status: form.status,
    };

    if (editId) {
      const existing = appoints.find((a) => a.id === editId);
      const noChange =
        existing &&
        existing.appointName?.trim() === payload.appointName &&
        existing.duration?.trim() === payload.duration &&
        (existing.probationPeriod?.trim() || "N/A") === payload.probationPeriod &&
        existing.status === payload.status;
      if (noChange) {
        toast.warn("No changes found for updation");
        return;
      }
    }

    try {
      if (editId) {
        await updateAppoint(editId, payload, token);
        toast.success("Appointment updated successfully");
      } else {
        await createAppoint(payload, token);
        toast.success("Appointment created successfully");
      }
      handleCloseModal();
      fetchAppoints();
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  const filtered = appoints.filter((a) =>
    a.appointName?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <Container fluid className="p-4">

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
        <div>
          <h5 className="page-main-heading">Appointments</h5>
          <small className="page-sub-heading">Manage employment types and appointment structures</small>
        </div>
        {/* Add Appointment button — color #2E5CB8 */}
        <Button
          onClick={handleOpenAddModal}
          style={{ borderRadius: "8px", fontWeight: 500, background: "#2E5CB8", border: "none" }}
        >
          <FiPlus className="me-1" /> Add Appointment
        </Button>
      </div>

      {/* Search */}
      <div className="mb-3 mt-3" style={{ maxWidth: 340 }}>
        <InputGroup>
          <InputGroup.Text style={{ background: "#fff" }}>
            <FiSearch color="#888" />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search appointment types..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ borderLeft: "none" }}
          />
        </InputGroup>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <Table hover responsive className="mb-0 align-middle" style={{ minWidth: 550 }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444" }}>Appointment Type</th>
                <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444" }}>Duration</th>
                <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444" }}>Probation Period</th>
                <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444", textAlign: "center" }}>Status</th>
                <th style={{ padding: "14px 20px", fontWeight: 600, color: "#444", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-muted">No appointments found</td>
                </tr>
              ) : (
                filtered.map((appoint) => (
                  <tr key={appoint.id}>
                    <td style={{ padding: "14px 20px", fontWeight: 500 }}>{appoint.appointName}</td>
                    <td style={{ padding: "14px 20px" }}>{appoint.duration || "—"}</td>
                    <td style={{ padding: "14px 20px" }}>{appoint.probationPeriod || "N/A"}</td>
                    <td style={{ padding: "14px 20px", textAlign: "center" }}>
                      <Badge
                        bg={appoint.status === "Active" ? "success" : "secondary"}
                        style={{ fontSize: "0.78rem", padding: "5px 12px", borderRadius: 20 }}
                      >
                        {appoint.status || "Active"}
                      </Badge>
                    </td>
                    {/* Delete button removed — only Edit remains */}
                    <td style={{ padding: "14px 20px", textAlign: "center" }}>
                      <button
                        onClick={() => handleEdit(appoint)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#4466ee" }}
                        title="Edit"
                      >
                        <FiEdit2 size={17} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="md">
        <Modal.Header closeButton style={{ borderBottom: "1px solid #eee" }}>
          <Modal.Title style={{ fontSize: "1.1rem", fontWeight: 600 }}>
            {editId ? "Edit Appointment" : "Add Appointment"}
          </Modal.Title>
        </Modal.Header>
        <p className="text-muted px-4 pt-2" style={{ fontSize: 12, marginBottom: 0 }}>
          {editId ? "Update the employment type details." : "Add a new employment type for your organization."}
        </p>

        <Modal.Body className="p-4">

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>
              Appointment Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="appointName"
              placeholder="e.g. Full-time Permanent"
              value={form.appointName}
              onChange={handleChange}
              style={{ borderRadius: 8, height: 42 }}
              isInvalid={!!errors.appointName}
              autoFocus
            />
            {errors.appointName && <small className="text-danger">{errors.appointName}</small>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>
              Duration <span className="text-danger">*</span>
            </Form.Label>
            <div style={{ position: "relative" }}>
              <Form.Select
                name="duration"
                value={form.duration}
                onChange={handleChange}
                style={{
                  borderRadius: 8,
                  height: 42,
                  appearance: "none",
                  paddingRight: 36,
                  borderColor: errors.duration ? "#dc3545" : "#dee2e6",
                }}
              >
                <option value="">Permanent</option>
                <option value="Permanent">Permanent</option>
                <option value="1 Month">1 Month</option>
                <option value="3 Months">3 Months</option>
                <option value="6 Months">6 Months</option>
                <option value="1 Year">1 Year</option>
                <option value="2 Years">2 Years</option>
              </Form.Select>
              <span style={{
                position: "absolute", right: 12, top: "50%",
                transform: "translateY(-50%)", pointerEvents: "none", color: "#aaa", fontSize: 16
              }}>›</span>
            </div>
            {errors.duration && <small className="text-danger">{errors.duration}</small>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>
              Probation Period
            </Form.Label>
            <div style={{ position: "relative" }}>
              <Form.Select
                name="probationPeriod"
                value={form.probationPeriod}
                onChange={handleChange}
                style={{
                  borderRadius: 8,
                  height: 42,
                  appearance: "none",
                  paddingRight: 36,
                }}
              >
                <option value="">N/A</option>
                <option value="1 Month">1 Month</option>
                <option value="2 Months">2 Months</option>
                <option value="3 Months">3 Months</option>
                <option value="6 Months">6 Months</option>
              </Form.Select>
              <span style={{
                position: "absolute", right: 12, top: "50%",
                transform: "translateY(-50%)", pointerEvents: "none", color: "#aaa", fontSize: 16
              }}>›</span>
            </div>
          </Form.Group>

          {/* Active Status toggle — green when Active */}
          <Form.Group className="mb-2">
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label className="fw-semibold mb-0" style={{ fontSize: 13 }}>
                Active Status
              </Form.Label>
              <div
                onClick={handleToggleStatus}
                style={{
                  width: 34,
                  height: 18,
                  borderRadius: 50,
                  background: form.status === "Active" ? "#22c55e" : "#ccc",
                  cursor: "pointer",
                  position: "relative",
                  transition: "background 0.2s",
                  flexShrink: 0,
                }}
              >
                <div style={{
                  position: "absolute",
                  top: 2,
                  left: form.status === "Active" ? 18 : 2,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: "#fff",
                  transition: "left 0.2s",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                }} />
              </div>
            </div>
          </Form.Group>

        </Modal.Body>

        <Modal.Footer style={{ borderTop: "1px solid #eee" }}>
          <Button variant="secondary" onClick={handleCloseModal} style={{ borderRadius: 7, minWidth: 90 }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} style={{ borderRadius: 7, minWidth: 90, background: "#2E5CB8", border: "none" }}>
            {editId ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </Container>
  );
};

export default Appoint;